<script setup lang="ts">
import {nextTick, ref, watchEffect, computed, useTemplateRef} from "vue";
import {useRoute, useRouter} from "vue-router";
import {
    Button,
    Card,
    FileUpload,
    InputText,
    Label,
    Select,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Textarea,
    ToggleSwitch,
} from "@/Components/Library";
import {PageLoading, PageTitle, StockImageSearch} from "@/Components/Global";
import {getOpengraphPreview, getStimulus, storeStimulus, updateStimulus} from "@/Services/stimuli";
import {useStorage, watchDebounced} from "@vueuse/core";
import {ResetButton} from "@/Components/Forms";
import {getting} from "@/Composables/api";
import {Icon} from "@iconify/vue";
import {useMainStore} from "@/Stores/store";
import User from "@/Models/User";
import {getQuestions} from "@/Services/questions";
import {useToast} from "primevue/usetoast";
import Stimulus from "@/Models/Stimulus";

const currentRoute = useRoute();
const router = useRouter();
const toast = useToast();
const $user = new User();
const $store = useMainStore();
const saving = ref(false);
const loading = ref(false);
const question = useStorage(`U${$user.id}:questionForm`, {});
const annotation = computed(() => {
    return currentRoute.query.for === "question" && question.value?.type === "annotation";
});
const defaultStimulus = {
    id: currentRoute.params.stimulus,
    title: null,
    description: null,
    type: annotation.value ? "slides" : "document",
    data: {
        document: null,
        slides: [],
        video: null,
        audio: null,
        image: [],
        url: null,
        question: null,
    },
    tags: [],
};
const storedStimulus = useStorage(`U${$user.id}:stimulusForm`, structuredClone(defaultStimulus));
console.log(question.value);
const stimulus = ref(new Stimulus(storedStimulus.value));
const stockImage = ref();
const coverImage = ref();
const questionOptions = ref([]);
const ogMeta = ref(null);
const uploadVideo = ref(false);
const documentUploadRef = ref<FileUpload>();
const slidesUploadRef = ref<FileUpload>();
const videoUploadRef = ref<FileUpload>();
const audioUploadRef = ref<FileUpload>();
const imageUploadRef = ref<FileUpload>();
const coverImageUploadRef = ref<FileUpload>();
const errors = ref({
    video: null,
});

if (currentRoute.params.stimulus) {
    loading.value = true;
    getStimulus(currentRoute.params.stimulus).then((data) => {
        console.log(data);
        stimulus.value = new Stimulus(data);
        console.log(stimulus.value);
        loading.value = false;
    });
} else {
    if (stimulus.value?.id) {
        stimulus.value = new Stimulus(defaultStimulus);
    }
}
console.log(stimulus.value, storedStimulus.value);
const save = async () => {
    try {
        saving.value = "Saving stimulus";
        errors.value = {
            video: null,
            url: null,
        };
        const urlCheck = new RegExp(
            /[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/gi,
        );

        if (stimulus.value.is("video") && stimulus.value.data && !urlCheck.test(stimulus.value.data)) {
            errors.value.video = "Please enter a valid video URL";
            toast.add({
                severity: "error",
                summary: "Invalid URL",
                detail: "Please enter a valid video URL",
                life: 3000,
            });
            return;
        }

        if (stimulus.value.is("url") && stimulus.value.data && !urlCheck.test(stimulus.value.data)) {
            errors.value.url = "Please enter a valid URL";
            toast.add({
                severity: "error",
                summary: "Invalid URL",
                detail: "Please enter a valid URL",
                life: 3000,
            });
            return;
        }
        console.log(stimulus.value);
        // const typeSlug = stimulus.value.type;
        // console.log(typeSlug);
        let postData = {
            id: stimulus.value.id,
            title: stimulus.value.title,
            description: stimulus.value.description,
            type: stimulus.value.type,
            data: stimulus.value.data,
            // tags: stimulus.value.tags.map((tag) => tag.name),
            stock_image: stimulus.value.is("image") ? stockImage.value?.id : null,
            // cover_stock_image: stimulus.value.is("audio") ? coverImage.value.id : null,
        };

        // console.log(postData, stimulus.value.data);
        // return;

        if (currentRoute.params.stimulus) {
            console.log(postData);
            await updateStimulus(stimulus.value, postData);
        } else {
            const data = await storeStimulus(currentRoute.params.group, postData);
            stimulus.value = new Stimulus(data);
            console.log(data, stimulus.value);
        }
        saving.value = "Uploading " + stimulus.value.type;
        await nextTick();

        switch (stimulus.value.type) {
            case "document":
                await documentUploadRef.value?.startUpload();
                break;
            case "slides":
                await slidesUploadRef.value?.startUpload();
                break;
            case "video":
                await videoUploadRef.value?.startUpload();
                break;
            case "audio":
                // if (!coverImage.value && coverImageUploadRef.value?.files?.length) {
                //     await coverImageUploadRef.value?.startUpload();
                // }
                await audioUploadRef.value?.startUpload();
                break;
            case "image":
                await imageUploadRef.value?.startUpload();
                break;
        }

        if (currentRoute.query.for === "question") {
            const question = useStorage(`U${$user.id}:questionForm`, {});
            question.value.stimulus_id = stimulus.value.id;

            let edit = question.value.id;
            let route = currentRoute.query.group ? `groups.questions.` : `questions.`;
            route += edit ? "edit" : "create";

            await router.push({
                name: route,
                params: {...currentRoute.params, question: edit, group: currentRoute.query.group},
                query: {...currentRoute.query, stimulus: stimulus.value.id},
            });
        } else {
            await router.push({name: "stimuli.show", params: {stimulus: stimulus.value.id}});
        }

        $store.toastMessages.push({
            severity: "success",
            summary: "Stimulus saved successfully",
            life: 3000,
        });
    } catch (e) {
        console.log(e);
    } finally {
        saving.value = false;
    }
};

const preview = () => {
    console.log(stimulus.value);
    if (["url", "video"].includes(stimulus.value.type) && stimulus.value.data) {
        ogMeta.value = null;
        //TODO validate url
        let url = stimulus.value.data;
        const regex = new RegExp(
            /[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/gi,
        );
        // console.log(url, regex.test(url));
        if (!regex.test(url)) {
            console.log("fail");
            return;
        }
        if (stimulus.value.is("video") && stimulus.value.data) {
            let embedUrl = stimulus.value.data;
            if (stimulus.value.data.toLowerCase().includes("yout")) {
                let ytregex = new RegExp(/(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/gm);
                let videoId = ytregex.exec(embedUrl)[3];
                if (videoId) {
                    embedUrl = `https://www.youtube.com/embed/${videoId}`;
                }
            }

            if (stimulus.value.data.toLowerCase().includes("vimeo")) {
                let vregex = new RegExp("(?:https?:)?(?:\/\/)?(?:www\.)?(?:vimeo)\.com(?:\/video)?\/([0-9]+)");
                let vmatch = embedUrl.match(vregex);
                console.log(vmatch);
                if (vmatch) {
                    embedUrl = `https://player.vimeo.com/video/${vmatch[1]}`;
                }
            }
            ogMeta.value = {"video:url": embedUrl};
        }
        console.log("ogMeta.value");
        getOpengraphPreview({url: stimulus.value.data})
            .then((data) => {
                console.log(data);
                if (!ogMeta.value) {
                    ogMeta.value = data;
                } else {
                    Object.assign(ogMeta.value, data);
                }
            })
            .catch((error) => {
                console.error(error);
                ogMeta.value = {
                    error: error,
                };
            });
    } else {
        ogMeta.value = null;
    }
};

const fileSelected = (e) => {
    let file = e.files?.[0];
    console.log(file, stimulus.value.type);
    if (stimulus.value.is("video") && file?.type === "video/quicktime") {
        alert("Quicktime videos are not supported. Please convert your video to mp4 or webm format and try again.");
        videoUploadRef.value?.clearFiles();
    }

    if (stimulus.value.is("audio") && file?.type === "video/mp4") {
        alert("Mp4 videos are not valid audio files. Please convert your video to an mp3 and try again.");
        videoUploadRef.value?.clearFiles();
    }
};

if (annotation.value) {
    stimulus.value = new Stimulus(defaultStimulus);
}

watchEffect(() => {
    if (stimulus.value.is("question")) {
        getQuestions().then(({data}) => (questionOptions.value = data));
    } else {
        preview();
    }

    storedStimulus.value = stimulus.value.stimulus;
});

watchDebounced(
    () => [stimulus.value.data],
    () => preview,
    {debounce: 2500},
);

const resetForm = () => {
    stimulus.value = new Stimulus(defaultStimulus);
    documentUploadRef.value?.clearFiles();
    slidesUploadRef.value?.clearFiles();
    videoUploadRef.value?.clearFiles();
    audioUploadRef.value?.clearFiles();
    imageUploadRef.value?.clearFiles();
    coverImageUploadRef.value?.clearFiles();
    stockImage.value = null;
    coverImage.value = null;
    errors.value = {video: null};
    ogMeta.value = null;
};

const stockImageSearchRef = useTemplateRef("stockImageSearchRef");
const clearStockImage = () => {
    stockImage.value = null;
    stockImageSearchRef.value.clearSearch();
};
</script>
<template>
    <div class="mx-auto w-full max-w-5xl pb-6">
        <PageTitle class="mb-2" :title="`${currentRoute.params?.stimulus ? 'Update' : 'Add'} stimulus`">
            <ResetButton @reset="resetForm" />
        </PageTitle>
        <form @submit.prevent="save">
            <Card class="lg:mb-4">
                <div class="mb-4 flex flex-col">
                    <Label for="title" class="mb-1" required>Title</Label>
                    <InputText
                        id="title"
                        name="title"
                        placeholder="Enter a name for your stimulus"
                        fluid
                        required
                        v-model="stimulus.title"
                    />
                </div>
                <div class="mb-4 flex flex-col">
                    <Label for="description" class="mb-1">Description</Label>
                    <Textarea
                        id="description"
                        name="description"
                        placeholder="Enter a description for your stimulus"
                        fluid
                        auto-resize
                        v-model="stimulus.description"
                    />
                </div>
            </Card>
            <!--            <div class="mb-4">-->
            <!--                <h3 class="text-sm font-bold">Current stimulus</h3>-->
            <!--                <StimulusDisplay ref="stimulusDisplay" :stimulus="stimulus.stimulus" />-->
            <!--            </div>-->
            <!--            <Card class="lg:mb-4">-->
            <!--                <TagSelect v-model="stimulus.tags" />-->
            <!--            </Card>-->
            <Card class="lg:mb-4">
                <div class="">
                    <Tabs v-model:value="stimulus.type" scrollable>
                        <TabList pt:nextbutton:type="button" pt:prevbutton:type="button">
                            <Tab v-if="!annotation" value="document">
                                <div class="flex items-center gap-2">
                                    <Icon icon="gravity-ui:logo-acrobat" width="18" />
                                    <span class="text-sm font-bold">Document</span>
                                </div>
                            </Tab>
                            <Tab value="slides">
                                <div class="flex items-center gap-2">
                                    <Icon icon="bi:images" width="18" />
                                    <span class="text-sm font-bold">Slides</span>
                                </div>
                            </Tab>
                            <Tab v-if="!annotation" value="video">
                                <div class="flex items-center gap-2">
                                    <Icon icon="ion:play" width="18" />
                                    <span class="text-sm font-bold">Video</span>
                                </div>
                            </Tab>
                            <Tab v-if="!annotation" value="audio">
                                <div class="flex items-center gap-2">
                                    <Icon icon="ion:play" width="18" />
                                    <span class="text-sm font-bold">Audio</span>
                                </div>
                            </Tab>
                            <Tab value="image">
                                <div class="flex items-center gap-2">
                                    <Icon icon="bi:image-alt" width="18" />
                                    <span class="text-sm font-bold">Image</span>
                                </div>
                            </Tab>
                            <Tab v-if="!annotation" value="url">
                                <div class="flex items-center gap-2">
                                    <Icon icon="iconamoon:link-bold" width="18" />
                                    <span class="text-sm font-bold">URL</span>
                                </div>
                            </Tab>
                            <!--                            <Tab value="question">-->
                            <!--                                <div class="flex items-center gap-2">-->
                            <!--                                    <Icon icon="bi:question" width="18" />-->
                            <!--                                    <span class="text-sm font-bold">Question</span>-->
                            <!--                                </div>-->
                            <!--                            </Tab>-->
                        </TabList>
                        <TabPanels>
                            <TabPanel value="document">
                                <p class="mb-2">Upload a PDF</p>
                                <FileUpload
                                    ref="documentUploadRef"
                                    id="document"
                                    name="document"
                                    collection="document"
                                    :parent-id="stimulus.id"
                                    parent-type="stimuli"
                                    accept="application/pdf"
                                />
                            </TabPanel>
                            <TabPanel value="slides">
                                <p class="mb-2">Upload up to 250 images to create a slide show (max 500MB in total)</p>
                                <FileUpload
                                    ref="slidesUploadRef"
                                    id="slides"
                                    name="slides"
                                    collection="slides"
                                    :parent-id="stimulus.id"
                                    parent-type="stimuli"
                                    accept="image/*"
                                    multiple
                                />
                            </TabPanel>
                            <TabPanel value="video">
                                <div class="mb-2 flex items-center justify-end gap-2">
                                    <Label for="upload_video">Upload video</Label>
                                    <ToggleSwitch input-id="upload_video" v-model="uploadVideo" />
                                </div>
                                <div v-auto-animate>
                                    <div v-if="!uploadVideo" v-auto-animate>
                                        <InputText
                                            name="video"
                                            placeholder="Video URL https://"
                                            v-model="stimulus.data"
                                            class="w-full"
                                            :class="{'!border-red-500': errors.video}"
                                            @input="errors.video = null"
                                        />
                                        <span v-if="errors.video" class="mt-1 text-sm font-medium text-red-500">
                                            {{ errors.video }}
                                        </span>
                                    </div>
                                    <FileUpload
                                        v-if="uploadVideo"
                                        ref="videoUploadRef"
                                        id="video"
                                        name="video"
                                        collection="video"
                                        :parent-id="stimulus.id"
                                        parent-type="stimuli"
                                        accept="video/*"
                                        @select="fileSelected"
                                    />
                                </div>
                                <!-- TODO: Please check if this max file size is OK -->
                            </TabPanel>
                            <TabPanel value="audio">
                                <Label for="audio" class="mb-1">Upload an audio file</Label>
                                <FileUpload
                                    ref="audioUploadRef"
                                    id="audio"
                                    name="audio"
                                    collection="audio"
                                    :parent-id="stimulus.id"
                                    parent-type="stimuli"
                                    accept="audio/*"
                                    @select="fileSelected"
                                />
                                <!--                                <div class="mt-6">-->
                                <!--                                    <h3 class="mb-2 text-lg font-bold">Cover image</h3>-->
                                <!--                                    <div>-->
                                <!--                                        <Label for="cover-image" class="mb-1">Select a stock image</Label>-->
                                <!--                                        <StockImageSearch-->
                                <!--                                            placeholder="Start typing to find royalty free images"-->
                                <!--                                            v-model="coverImage"-->
                                <!--                                        />-->
                                <!--                                    </div>-->
                                <!--                                    <p class="text-center">or</p>-->
                                <!--                                    <div>-->
                                <!--                                        <Label for="cover-image" class="mb-1">Upload an image</Label>-->
                                <!--                                        <FileUpload-->
                                <!--                                            ref="coverImageUploadRef"-->
                                <!--                                            id="cover-image"-->
                                <!--                                            name="image"-->
                                <!--                                            collection="cover"-->
                                <!--                                            :parent-id="stimulus.id"-->
                                <!--                                            parent-type="stimuli"-->
                                <!--                                            accept="image/*"-->
                                <!--                                            :disabled="!!coverImage"-->
                                <!--                                            :disabled-message="`Stimulus can only have one cover. Please, remove the stock image to upload your own.`"-->
                                <!--                                        />-->
                                <!--                                    </div>-->
                                <!--                                </div>-->
                            </TabPanel>
                            <TabPanel value="image">
                                <StockImageSearch
                                    ref="stockImageSearchRef"
                                    class="mb-6"
                                    placeholder="Start typing to find royalty free images, or upload your own below"
                                    v-model="stockImage"
                                />
                                <div class="flex justify-center" v-if="stockImage">
                                    <Button
                                        class="border border-stone-400 text-xs hover:brightness-110 dark:border-stone-500"
                                        @click="clearStockImage"
                                    >
                                        Upload custom image instead
                                    </Button>
                                </div>
                                <FileUpload
                                    v-else
                                    ref="imageUploadRef"
                                    id="image"
                                    name="image"
                                    collection="image"
                                    :parent-id="stimulus.id"
                                    parent-type="stimuli"
                                    accept="image/*"
                                    multiple
                                />
                                <div v-if="stimulus.files?.[0]?.url" class="mt-4">
                                    <h3 class="text-sm font-bold">Current image</h3>
                                    <img
                                        :src="stimulus.files?.[0]?.url"
                                        alt="Stimulus Image"
                                        class="h-auto w-full rounded-lg object-cover"
                                    />
                                </div>
                            </TabPanel>
                            <TabPanel value="url">
                                <div v-auto-animate>
                                    <InputText
                                        name="url"
                                        placeholder="URL https://"
                                        v-model="stimulus.data"
                                        class="w-full"
                                        :class="{'!border-red-500': errors.url}"
                                        @input="errors.url = null"
                                    />
                                    <span v-if="errors.url" class="mt-1 text-sm font-medium text-red-500">
                                        {{ errors.url }}
                                    </span>
                                </div>
                            </TabPanel>
                            <TabPanel value="question">
                                <div class="flex flex-col">
                                    <Label for="question" class="mb-1" required>
                                        Select a question to use as a stimulus
                                    </Label>
                                    <Select
                                        input-id="question"
                                        name="question"
                                        v-model="stimulus.data"
                                        :options="questionOptions"
                                        option-label="title"
                                        option-value="id"
                                        placeholder="Select a question"
                                        required
                                        checkmark
                                        fluid
                                    >
                                        <template #option="{option}">
                                            <div class="flex w-full min-w-0 max-w-2xl flex-col gap-1 px-2">
                                                <span class="whitespace-break-spaces text-sm font-semibold">
                                                    {{ option.title }}
                                                </span>
                                                <span class="w-full min-w-0 max-w-full shrink text-wrap text-xs">
                                                    {{ option.type.name }}
                                                </span>
                                            </div>
                                        </template>
                                    </Select>
                                </div>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                    <div>
                        <template v-if="['url', 'video'].includes(stimulus.type) && stimulus.data">
                            <PageLoading class="w-16 text-askr-green" v-if="getting.includes('opengraph') && !ogMeta" />
                            <div v-if="ogMeta && Object.keys(ogMeta).length">
                                <h3 class="mb-1 mt-4 font-bold">PREVIEW</h3>
                                <div class="w-full items-center justify-center">
                                    <template v-if="ogMeta">
                                        <template v-if="ogMeta.error">
                                            {{
                                                ogMeta.error.response?.data?.message ??
                                                ogMeta.error.message ??
                                                "An error occured"
                                            }}
                                        </template>
                                        <template v-else-if="ogMeta['video:url']">
                                            <iframe
                                                :src="ogMeta['video:url']"
                                                class="mb-2 aspect-video w-full rounded-lg"
                                                allowfullscreen
                                            ></iframe>
                                        </template>
                                        <template v-else-if="ogMeta?.image">
                                            <a :href="stimulus.data" target="_blank" class="">
                                                <img
                                                    :src="ogMeta.image"
                                                    alt="Stimulus Image"
                                                    class="mb-2 h-auto w-full rounded-lg object-cover"
                                                />
                                            </a>
                                        </template>
                                        <template v-if="ogMeta">
                                            <h4 class="mb-1 text-lg font-semibold">{{ ogMeta?.title }}</h4>
                                            <p class="text-sm">{{ ogMeta?.description }}</p>
                                        </template>
                                    </template>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </Card>
            <div class="">
                <Button
                    :loading="!!saving"
                    type="submit"
                    class="w-full bg-askr-green text-white"
                    icon="fluent:checkmark-12-regular"
                >
                    {{ saving || "Save" }}
                </Button>
            </div>
        </form>
        <!--        <pre>-->
        <!--            {{ stimulus }}-->
        <!--        </pre>-->
    </div>
</template>
<style scoped></style>
